import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  Camera,
  ChevronRight,
  GraduationCap,
  MapPin,
  Save,
  Sparkles,
  Trash2,
  UserCircle2,
  Users,
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { apiService } from '../services/api';
import { LANGUAGE_OPTIONS, SKILL_OPTIONS, getRestDayOptions, getAccommodationOptions } from '../constants/formOptions';
import { DATA_DICTIONARY_TYPES, useDataDictionaries } from '../hooks/useDataDictionary';
import { CookingSkill, CreateHelperRequest, DataDictionary, HelperProfile, User, WorkExperience } from '../types';
import { getDictDisplayValue, resolveApiError } from '../utils/i18n';
import { CookingProficiencyTooltip } from '../components/CookingProficiencyTooltip';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatExperienceSummary } from '@/utils/experienceSummary';
import { Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MultiSelectPills } from '@/components/ui/multi-select-pills';
import { FileUploadList } from '@/components/ui/file-upload-list';
import type { DocumentType, HelperDocumentItem } from '@/components/ui/file-upload-list';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';

// ponytail: inline date picker — use local date formatting to avoid UTC-offset off-by-one
const localFmt = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseLocalDate = (s: string) => (s ? new Date(s + 'T00:00:00') : undefined);

const DatePickerField = ({
  value,
  onChange,
  disabled,
  className,
  startMonth,
  endMonth,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  className?: string;
  startMonth?: Date;
  endMonth?: Date;
}) => {
  const selected = parseLocalDate(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className={`justify-start text-left font-normal h-10 rounded-xl bg-background border-border ${className ?? ''}`}>
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? localFmt(selected) : <span className="text-muted-foreground">YYYY-MM-DD</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => onChange(d ? localFmt(d) : '')}
          initialFocus
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
        />
      </PopoverContent>
    </Popover>
  );
};

type ActiveSection = 'general' | 'about' | 'education' | 'working-experience' | 'skills';

type ExperienceDocsMap = Record<number, Partial<Record<DocumentType, HelperDocumentItem[]>>>;

type Option = {
  value: string;
  label: string;
};

const optionalNumberSchema = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z.number().finite().nonnegative().optional(),
);

const requiredNumberSchema = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? 0 : Number(value)),
  z.number().finite().nonnegative(),
);

const genderSchema = z.string().min(1);
const maritalStatusSchema = z.string().min(1);
const cookingSkillSchema = z.string();

// 驗證訊息要跟隨介面語言，schema 以 factory 形式接收 t（同 getLoginSchema 慣例）
const buildHelperProfileSchema = (t: (key: string, defaultValue: string) => string) => {

const workExperienceSchema = z.object({
  id: z.number().optional(),
  title: z.string(),
  location: z.string(),
  startDate: z.string().min(1, t('helperProfileForm.valStartDateRequired', '開始日期不能為空')).refine(
    (val) => {
      // 支援 YYYY-MM / YYYY/MM / YYYY-MM-DD / YYYY/MM/DD
      const m = val.match(/^(\d{4})[-/](\d{1,2})(?:[-/](\d{1,2}))?$/);
      if (!m) return true; // 格式不認得就交給 backend 判
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3] || '1'));
      return d <= new Date();
    },
    { message: t('helperProfileForm.valStartDateFuture', '開始日期不能是未來的日期') }
  ),
  endDate: z.string().optional(),
  isCurrent: z.boolean(),
  employerFamily: z.string().optional(),
  notes: z.array(z.string()).default([]),
  skills: z.array(z.string()).default([]),
  familySize: z.object({
    adults: requiredNumberSchema,
    newborn: requiredNumberSchema,
    infant: requiredNumberSchema,
    children: requiredNumberSchema,
    teen: requiredNumberSchema,
    elderly: requiredNumberSchema,
    pets: requiredNumberSchema,
  }),
  unitSize: optionalNumberSchema,
  numberOfEmployers: optionalNumberSchema,
  hasRecommendationLetter: z.boolean().optional().default(false),
  contractEndReason: z.enum(['COMPLETED', 'EARLY_TERMINATION', 'DISMISSED', 'OTHER', '']).optional(),
  contractEndReasonNote: z.string().optional(),
});

return z.object({
  userId: requiredNumberSchema,
  fullName: z.string().trim().min(1, t('helperProfileForm.valFullNameRequired', '姓名不能為空')),
  gender: genderSchema,
  dateOfBirth: z.string().min(1, t('helperProfileForm.valDobRequired', '出生日期不能為空')).refine((val) => {
    const dob = new Date(val);
    if (Number.isNaN(dob.getTime())) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dob >= today) return false;
    const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    return dob <= eighteenYearsAgo;
  }, { message: t('helperProfileForm.valDobAdult', '出生日期不可為今天或未來日期，且年齡必須滿 18 歲') }),
  nationality: z.string().min(1, t('helperProfileForm.valNationalityRequired', '國籍不能為空')),
  passportNumber: z.string().trim().optional().or(z.literal('')),
  passportExpiryDate: z.string().optional().refine((val) => {
    if (!val) return true;
    const expiry = new Date(val);
    if (Number.isNaN(expiry.getTime())) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expiry > today;
  }, { message: t('helperProfileForm.valPassportExpiryFuture', '護照到期日必須是未來的日期') }),
  idNumber: z.string().trim().optional().refine((val) => !val || /^[A-Za-z]{1,2}\d{6}\([0-9Aa]\)$/.test(val), {
    message: t('helperProfileForm.valIdNumberFormat', '身分證號碼格式不正確，須為 1-2 個英文字母加 6 個數字加括號校驗碼（例如 A123456(3)）'),
  }),
  // 唯讀欄位，值同步自帳號登入電話（開戶時已驗證過格式），不再重複驗證以免舊格式號碼卡住儲存
  whatsappPhone: z.string().optional().or(z.literal('')),
  secondaryPhone: z.string().optional(),
  helperCategory: z.enum(['LOCAL_COMPLETED', 'OVERSEAS_NEW', 'OVERSEAS_HELPER', 'OTHER']).optional(),
  email: z.string().email(t('helperProfileForm.valEmailInvalid', '電子郵件格式不正確')).optional().or(z.literal('')),
  maritalStatus: maritalStatusSchema,
  numberOfChildren: requiredNumberSchema,
  childrenAgesList: z.array(z.string()).default([]),
  numberOfSiblings: optionalNumberSchema,
  siblingRank: optionalNumberSchema,
  spouseOccupation: z.string().optional(),
  hometownRegion: z.string().optional(),
  religion: z.string().optional(),
  educationLevel: z.string().min(1, t('helperProfileForm.valEducationRequired', '教育程度不能為空')),
  educationInstitution: z.string().optional(),
  educationDegree: z.string().optional(),
  educationStartYear: z.string().optional(),
  educationStartMonth: z.string().optional(),
  educationEndYear: z.string().optional(),
  educationEndMonth: z.string().optional(),
  educationOngoing: z.boolean().optional().default(false),
  height: optionalNumberSchema,
  weight: optionalNumberSchema,
  cookingSkills: z.array(cookingSkillSchema).default([]),
  cookingSkillsText: z.array(z.string()).default([]),
  languageSkills: z.array(z.string()).min(1, t('helperProfileForm.valLanguageRequired', '請至少選擇一種語言')),
  otherSkills: z.array(z.string()).default([]),
  careTypes: z.array(z.string()).default([]),
  houseworkTypes: z.array(z.string()).default([]),
  expectedSalary: requiredNumberSchema,
  expectedSalaryCurrency: z.string().min(1),
  workType: z.string().optional(),
  jobTitle: z.string().optional(),
  restDayPreference: z.string().min(1),
  accommodation: z.string().min(1),
  preferredWorkLocation: z.string().optional(),
  availableStartDate: z.string().optional(),
  lookingForJob: z.boolean(),
  willingWeekdayOff: z.boolean().nullish(),
  eatPork: z.boolean().nullish(),
  willingCarePets: z.boolean().nullish(),
  tutoringExperience: z.boolean().nullish(),
  needReturnHomeNextYear: z.boolean().nullish(),
  previousAdultAtHome: z.boolean().nullish(),
  previousCookDaily: z.boolean().nullish(),
  willingCareNewborn: z.boolean().nullish(),
  willingCareChild: z.boolean().nullish(),
  willingCareElderly: z.boolean().nullish(),
  willingCareDisabled: z.boolean().nullish(),
  selfIntroduction: z.string().trim().min(1, t('helperProfileForm.valSelfIntroRequired', '自我介紹不能為空')),
  workExperience: z.string().optional(),
  workExperiences: z.array(workExperienceSchema).default([]),
}).superRefine((values, ctx) => {
  // ponytail: 菲律賓/印尼護照號碼固定為 1 個英文字母 + 7 個數字
  const PH_ID_PASSPORT_REGEX = /^[A-Za-z]\d{7}$/;
  if (
    (values.nationality === 'PHILIPPINES' || values.nationality === 'INDONESIA') &&
    values.passportNumber &&
    !PH_ID_PASSPORT_REGEX.test(values.passportNumber.trim())
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['passportNumber'],
      message: t('helperProfileForm.valPassportPhFormat', '護照號碼格式不正確，須為 1 個英文字母加 7 個數字（例如 P1234567）'),
    });
  }
});
};

// 型別與訊息語言無關，用 fallback 版本建一次供型別推導
const helperProfileSchemaForTypes = buildHelperProfileSchema((_k, d) => d);
type HelperProfileFormValues = z.input<typeof helperProfileSchemaForTypes>;
type HelperProfileSubmitValues = z.output<typeof helperProfileSchemaForTypes>;

// 工作經驗的地點是曾工作的國家/地區，與「偏好工作地點」（香港分區）分開；
// value 存中文（與既有資料一致），label 走 i18n
const WORK_EXPERIENCE_LOCATIONS: { value: string; labelKey: string }[] = [
  { value: 'HK',    labelKey: 'helperProfileForm.locHongKong' },
  { value: 'SG',    labelKey: 'helperProfileForm.locSingapore' },
  { value: 'TW',    labelKey: 'helperProfileForm.locTaiwan' },
  { value: 'MY',    labelKey: 'helperProfileForm.locMalaysia' },
  { value: 'ME',    labelKey: 'helperProfileForm.locMiddleEast' },
  { value: 'HOME',  labelKey: 'helperProfileForm.locHomeCountry' },
  { value: 'OTHER', labelKey: 'helperProfileForm.locOther' },
];

const SECTION_LABELS: { id: ActiveSection; labelKey: string; labelFallback: string; descKey: string; descFallback: string }[] = [
  { id: 'general', labelKey: 'helperProfileForm.generalInformation', labelFallback: 'General information', descKey: 'helperProfileForm.navGeneralDesc', descFallback: 'Identity, contact, responsibilities' },
  { id: 'about', labelKey: 'helperProfileForm.about', labelFallback: 'About', descKey: 'helperProfileForm.navAboutDesc', descFallback: 'Personal story, availability, and education' },
  { id: 'working-experience', labelKey: 'helperProfileForm.workingExperiences', labelFallback: 'Working experiences', descKey: 'helperProfileForm.navWorkDesc', descFallback: 'Structured household history and skills' },
];

const GENDER_OPTIONS: Option[] = [
  { value: 'FEMALE', label: 'Female' },
  { value: 'MALE', label: 'Male' },
];


// ponytail: locked to HKD until multi-region support is enabled (matches JobPostingFormPage)
const SALARY_CURRENCY_OPTIONS: Option[] = [
  { value: 'HKD', label: 'HK$' },
];

const FAMILY_COUNT_OPTIONS = Array.from({ length: 7 }, (_, index) => ({
  value: String(index),
  label: String(index),
}));

const CHILD_COUNT_OPTIONS = Array.from({ length: 6 }, (_, index) => ({
  value: String(index),
  label: String(index),
}));

const createEmptyWorkExperience = (): HelperProfileFormValues['workExperiences'][number] => ({
  title: '',
  employerFamily: '',
  location: '',
  startDate: '',
  endDate: '',
  isCurrent: false,
  notes: [],
  skills: [],
  familySize: {
    adults: 0,
    newborn: 0,
    infant: 0,
    children: 0,
    teen: 0,
    elderly: 0,
    pets: 0,
  },
  unitSize: undefined,
  numberOfEmployers: undefined,
  hasRecommendationLetter: false,
  contractEndReason: '',
  contractEndReasonNote: '',
});

const buildDefaultValues = (user?: User | null): HelperProfileFormValues => ({
  userId: user?.id ?? 0,
  fullName: '',
  gender: 'FEMALE',
  height: undefined,
  weight: undefined,
  dateOfBirth: '',
  nationality: '',
  passportNumber: '',
  passportExpiryDate: '',
  idNumber: '',
  whatsappPhone: user?.whatsappPhone ?? '',
  secondaryPhone: '',
  helperCategory: undefined,
  email: user?.email ?? '',
  maritalStatus: 'SINGLE',
  numberOfChildren: 0,
  childrenAgesList: [],
  numberOfSiblings: undefined,
  siblingRank: undefined,
  spouseOccupation: '',
  hometownRegion: '',
  religion: '',
  educationLevel: 'SECONDARY',
  educationInstitution: '',
  educationDegree: '',
  educationStartYear: '',
  educationStartMonth: '',
  educationEndYear: '',
  educationEndMonth: '',
  educationOngoing: false,
  cookingSkills: [],
  cookingSkillsText: [],
  languageSkills: [],
  otherSkills: [],
  careTypes: [],
  houseworkTypes: [],
  expectedSalary: 0,
  expectedSalaryCurrency: 'HKD',
  workType: '',
  jobTitle: '',
  restDayPreference: 'ANY',
  accommodation: 'SEPARATE_ROOM',
  preferredWorkLocation: 'ANY',
  availableStartDate: '',
  lookingForJob: false,
  willingWeekdayOff: undefined,
  eatPork: undefined,
  willingCarePets: undefined,
  tutoringExperience: undefined,
  needReturnHomeNextYear: undefined,
  previousAdultAtHome: undefined,
  previousCookDaily: undefined,
  willingCareNewborn: undefined,
  willingCareChild: undefined,
  willingCareElderly: undefined,
  willingCareDisabled: undefined,
  selfIntroduction: '',
  workExperience: '',
  workExperiences: [],
});

const parseCommaString = (value?: string | null): string[] =>
  value
    ?.split(',')
    .map((item) => item.trim())
    .filter(Boolean) ?? [];

const mapSelectionsFromOptions = (values: string[], options: Option[], aliasMap?: Record<string, string>) => {
  const labelLookup = new Map(options.map((option) => [option.label.toLowerCase(), option.value]));

  return values.map((value) => {
    if (options.some((option) => option.value === value)) {
      return value;
    }

    const aliased = aliasMap?.[value];
    if (aliased) {
      return aliased;
    }

    const labelMatch = labelLookup.get(value.toLowerCase());
    return labelMatch ?? value;
  });
};

const mapProfileToFormValues = (
  profile: HelperProfile,
  user: User | null | undefined,
  languageOptions: Option[],
  skillOptions: Option[],
): HelperProfileFormValues => ({
  userId: profile.userId ?? user?.id ?? 0,
  fullName: profile.fullName ?? '',
  gender: profile.gender ?? 'FEMALE',
  height: profile.height ?? undefined,
  weight: profile.weight ?? undefined,
  dateOfBirth: profile.dateOfBirth ?? '',
  nationality: profile.nationality ?? '',
  passportNumber: profile.passportNumber ?? '',
  passportExpiryDate: (profile as any).passportExpiryDate ?? '',
  idNumber: (profile as any).idNumber ?? '',
  whatsappPhone: profile.whatsappPhone ?? user?.whatsappPhone ?? '',
  secondaryPhone: profile.secondaryPhone ?? '',
  helperCategory: (profile.helperCategory as any) ?? undefined,
  email: profile.email ?? user?.email ?? '',
  maritalStatus: profile.maritalStatus ?? 'SINGLE',
  numberOfChildren: profile.numberOfChildren ?? 0,
  childrenAgesList: parseCommaString((profile as any).childrenAges),
  numberOfSiblings: (profile as any).numberOfSiblings ?? undefined,
  siblingRank: (profile as any).siblingRank ?? undefined,
  spouseOccupation: (profile as any).spouseOccupation ?? '',
  hometownRegion: (profile as any).hometownRegion ?? '',
  religion: profile.religion ?? '',
  educationLevel: profile.educationLevel ?? 'SECONDARY',
  educationInstitution: profile.educationInstitution ?? '',
  educationDegree: profile.educationDegree ?? '',
  educationStartYear: profile.educationStartYear ? String(profile.educationStartYear) : '',
  educationStartMonth: profile.educationStartMonth ? String(profile.educationStartMonth) : '',
  educationEndYear: profile.educationEndYear ? String(profile.educationEndYear) : '',
  educationEndMonth: profile.educationEndMonth ? String(profile.educationEndMonth) : '',
  educationOngoing: profile.educationOngoing ?? false,
  cookingSkills: profile.cookingSkills ?? [],
  cookingSkillsText: profile.cookingSkillsText ? profile.cookingSkillsText.split(',').filter(Boolean) : [],
  languageSkills: parseCommaString(profile.languageSkills),
  otherSkills: mapSelectionsFromOptions(parseCommaString(profile.skills), skillOptions),
  careTypes: parseCommaString(profile.careTypes),
  houseworkTypes: parseCommaString(profile.houseworkTypes),
  expectedSalary: profile.expectedSalary ?? 0,
  expectedSalaryCurrency: profile.expectedSalaryCurrency ?? 'HKD',
  workType: profile.workType ?? '',
  jobTitle: profile.jobTitle ?? '',
  restDayPreference: profile.restDayPreference ?? 'ANY',
  accommodation: (profile as HelperProfile & { accommodation?: string }).accommodation ?? 'SEPARATE_ROOM',
  preferredWorkLocation: profile.preferredWorkLocation ?? 'ANY',
  availableStartDate: profile.availableStartDate ?? '',
  lookingForJob: profile.lookingForJob ?? false,
  willingWeekdayOff: profile.willingWeekdayOff,
  eatPork: profile.eatPork,
  willingCarePets: profile.willingCarePets,
  tutoringExperience: profile.tutoringExperience,
  needReturnHomeNextYear: profile.needReturnHomeNextYear,
  previousAdultAtHome: profile.previousAdultAtHome,
  previousCookDaily: profile.previousCookDaily,
  willingCareNewborn: profile.willingCareNewborn,
  willingCareChild: profile.willingCareChild,
  willingCareElderly: profile.willingCareElderly,
  willingCareDisabled: profile.willingCareDisabled,
  selfIntroduction: profile.selfIntroduction ?? '',
  workExperience: profile.workExperience ?? '',
  workExperiences:
    profile.workExperiences && profile.workExperiences.length > 0
      ? profile.workExperiences.map((experience) => ({
          id: experience.id,
          title: experience.title ?? '',
          employerFamily: experience.employerFamily ?? '',
          location: experience.location ?? '',
          startDate: experience.startDate ?? '',
          endDate: experience.endDate ?? '',
          isCurrent: experience.isCurrent ?? false,
          notes: experience.notes ?? [],
          skills: experience.skills ?? [],
          familySize: {
            adults: experience.familySize?.adults ?? 0,
            newborn: experience.familySize?.newborn ?? 0,
            infant: experience.familySize?.infant ?? 0,
            children: experience.familySize?.children ?? 0,
            teen: experience.familySize?.teen ?? 0,
            elderly: experience.familySize?.elderly ?? 0,
            pets: experience.familySize?.pets ?? 0,
          },
          unitSize: (experience as any).unitSizeSqft ?? undefined,
          numberOfEmployers: (experience as any).numberOfEmployers ?? undefined,
          hasRecommendationLetter: experience.hasRecommendationLetter ?? false,
          contractEndReason: (experience as any).contractEndReason ?? '',
          contractEndReasonNote: (experience as any).contractEndReasonNote ?? '',
        }))
      : [],
});

const getOptionsFromDictionaries = (dictionaries?: DataDictionary[] | null, fallback: Option[] = []): Option[] => {
  if (!dictionaries || dictionaries.length === 0) {
    return fallback;
  }

  return dictionaries.map((dictionary) => ({
    value: dictionary.code,
    label: getDictDisplayValue(dictionary),
  }));
};

const toDelimitedString = (values: string[]) => values.filter(Boolean).join(',');

const getSectionCompletion = (section: ActiveSection, values: HelperProfileFormValues, avatarUrl: string) => {
  switch (section) {
    case 'general': {
      const total = 11;
      const completed = [
        avatarUrl,
        values.fullName,
        values.gender,
        values.dateOfBirth,
        values.nationality,
        values.whatsappPhone,
        values.passportNumber,
        values.religion,
        Number(values.expectedSalary ?? 0) > 0 ? 'yes' : '',
        values.accommodation,
        values.availableStartDate,
      ].filter((value) => String(value ?? '').trim() !== '').length;
      return Math.round((completed / total) * 100);
    }
    case 'about': {
      const total = 1;
      const completed = [values.selfIntroduction].filter((value) => String(value ?? '').trim() !== '').length;
      return Math.round((completed / total) * 100);
    }
    case 'education': {
      const total = 4;
      const completed = [
        values.educationLevel,
        values.educationInstitution,
        values.educationDegree,
        (values.educationStartYear && values.educationStartMonth && (values.educationOngoing || (values.educationEndYear && values.educationEndMonth))),
      ].filter((value) => String(value ?? '').trim() !== '').length;
      return Math.round((completed / total) * 100);
    }
    case 'working-experience': {
      const experiences = values.workExperiences ?? [];
      if (experiences.length === 0) return 0;
      // 逐欄位計算：每段經驗的每個細項各佔一份
      let total = 0;
      let completed = 0;
      for (const exp of experiences) {
        const fields = [
          exp.title?.trim(),
          exp.employerFamily?.trim(),
          exp.location?.trim(),
          Number(exp.unitSize ?? 0) > 0,
          Number(exp.numberOfEmployers ?? 0) > 0,
          exp.startDate,
          exp.isCurrent || exp.endDate,
          exp.contractEndReason,
          (exp.skills?.length ?? 0) > 0,
          exp.notes?.some((n) => n?.trim()),
        ];
        total += fields.length;
        completed += fields.filter(Boolean).length;
      }
      return Math.round((completed / total) * 100);
    }
    case 'skills': {
      const total = 2;
      const completed = [
        values.languageSkills.length > 0,
        values.cookingSkills.length > 0,
      ].filter(Boolean).length;
      return Math.round((completed / total) * 100);
    }
    default:
      return 0;
  }
};

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) {
    return null;
  }

  return <p className="mt-2 text-xs font-medium text-red-600">{message}</p>;
};

const SectionHeading: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  completion: number;
  rightExtra?: React.ReactNode;
}> = ({ icon, title, description, completion, rightExtra }) => {
  const { t } = useTranslation();
  return (
    <div className="mb-3 flex flex-col gap-2 border-b border-border pb-3 md:flex-row md:items-center md:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">{icon}</div>
        <div>
          <h2 className="font-serif text-lg tracking-tight text-foreground">{title}</h2>
          <p className="mt-1 max-w-2xl text-xs leading-5 text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {rightExtra}
        <Badge className="w-fit rounded-full bg-primary/10 px-3 py-1 text-primary hover:bg-primary/10">
          {t('helperProfileForm.sectionCompletion', { pct: completion, defaultValue: `${completion}% 已完成` })}
        </Badge>
      </div>
    </div>
  );
};

const FormLabel: React.FC<{ children: React.ReactNode; hint?: string }> = ({ children, hint }) => (
  <label className="mb-2 block text-sm font-semibold text-foreground">
    {children}
    {hint ? <span className="ml-2 font-normal text-muted-foreground">{hint}</span> : null}
  </label>
);



const PillButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'rounded-full border px-4 py-2 text-sm font-medium transition duration-200',
      active
        ? 'border-primary bg-primary text-white shadow-[0_0_0_4px_rgba(45,106,92,0.14)]'
        : 'border-border bg-card text-card-foreground text-muted-foreground hover:border-primary/40 hover:text-primary',
    )}
  >
    {children}
  </button>
);



const FormSelect = ({ control, name, options, placeholder, setValueAs }: { control: any, name: string, options: {value: string, label: string}[], placeholder?: string, setValueAs?: (val: any) => any }) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <Select 
        onValueChange={(val) => {
           field.onChange(setValueAs ? setValueAs(val) : val);
        }} 
        value={field.value !== undefined && field.value !== '' ? String(field.value) : undefined}
      >
        <SelectTrigger className="bg-background border-border">
          <SelectValue placeholder={placeholder || "Select"} />
        </SelectTrigger>
        <SelectContent>
          {placeholder && <SelectItem value="empty_placeholder" disabled>{placeholder}</SelectItem>}
          {options.map(opt => (
            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
  />
);

const HelperProfileFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { showAlert } = useAlert();
  const { t, i18n } = useTranslation();
  const maritalStatusOptions = useMemo(() => [
    { value: 'SINGLE',   label: t('helperProfileForm.maritalSingle',   '未婚') },
    { value: 'MARRIED',  label: t('helperProfileForm.maritalMarried',  '已婚') },
    { value: 'DIVORCED', label: t('helperProfileForm.maritalDivorced', '離婚') },
    { value: 'WIDOWED',  label: t('helperProfileForm.maritalWidowed',  '喪偶') },
  ], [t]);
  const accommodationOptions = useMemo(() => getAccommodationOptions(t), [t]);
  const isEditMode = Boolean(id);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [activeSection, setActiveSection] = useState<ActiveSection>('general');
  // 編輯模式：初始即為 loading，避免先渲染空表單再切到 loading 的閃爍
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [serverError, setServerError] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [isDraggingAvatar, setIsDraggingAvatar] = useState(false);
  const [jobType, setJobType] = useState<'domestic-helper' | 'driver'>('domestic-helper');
  const [idProofDocuments, setIdProofDocuments] = useState<HelperDocumentItem[]>([]);
  const [eduCertDocuments, setEduCertDocuments] = useState<HelperDocumentItem[]>([]);
  const [overlappingIndices, setOverlappingIndices] = useState<number[]>([]);
  const [experienceDocs, setExperienceDocs] = useState<ExperienceDocsMap>({});
  const [legalMinWage, setLegalMinWage] = useState<{ amount: number; currency: string } | null>(null);
  const rawProfileRef = useRef<HelperProfile | null>(null);

  const getDocs = (idx: number, type: DocumentType): HelperDocumentItem[] =>
    experienceDocs[idx]?.[type] ?? [];
  const setDocs = (idx: number, type: DocumentType, docs: HelperDocumentItem[]) =>
    setExperienceDocs((prev) => ({ ...prev, [idx]: { ...prev[idx], [type]: docs } }));

  const { dictionariesByParent } = useDataDictionaries({
    parentCodes: [
      DATA_DICTIONARY_TYPES.NATIONALITY,
      DATA_DICTIONARY_TYPES.COOKING_SKILLS,
      DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE,
      DATA_DICTIONARY_TYPES.HOUSEWORK,
      DATA_DICTIONARY_TYPES.RELIGION,
      DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS,
      DATA_DICTIONARY_TYPES.OTHER_SKILLS,
      DATA_DICTIONARY_TYPES.JOB_LOCATION,
      DATA_DICTIONARY_TYPES.EDUCATION_LEVEL,
      DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL,
      DATA_DICTIONARY_TYPES.LANGUAGE_PROFICIENCY,
      DATA_DICTIONARY_TYPES.WORK_TYPE,
      DATA_DICTIONARY_TYPES.JOB_TITLE,
      DATA_DICTIONARY_TYPES.GENDER,
    ],
  });

  const nationalityOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.NATIONALITY)),
    [dictionariesByParent, i18n.language],
  );
  const cookingOptions = useMemo(
    () =>
      getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.COOKING_SKILLS)).map((option) => ({
        value: option.value as CookingSkill,
        label: option.label,
      })),
    [dictionariesByParent, i18n.language],
  );
  const careOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE)),
    [dictionariesByParent, i18n.language],
  );
  const houseworkOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.HOUSEWORK)),
    [dictionariesByParent, i18n.language],
  );
  const religionOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.RELIGION)),
    [dictionariesByParent, i18n.language],
  );
  const languageOptions = useMemo(
    () =>
      getOptionsFromDictionaries(
        dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS),
        LANGUAGE_OPTIONS.map((option) => ({ value: option.key, label: option.label })),
      ),
    [dictionariesByParent, i18n.language],
  );
  const proficiencyOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_PROFICIENCY)),
    [dictionariesByParent, i18n.language],
  );
  const cookingProficiencyOptions = useMemo(() => [
    { value: 'BEGINNER',     label: t('cookingLevel.beginner',      '新手') },
    { value: 'INTERMEDIATE', label: t('cookingLevel.intermediate',  '中階熟手') },
    { value: 'ADVANCED',     label: t('cookingLevel.advanced',      '高階專家') },
  ], [t]);
  const skillOptions = useMemo(
    () =>
      getOptionsFromDictionaries(
        dictionariesByParent.get(DATA_DICTIONARY_TYPES.OTHER_SKILLS),
        SKILL_OPTIONS.map((option) => ({ value: option.key, label: option.label })),
      ),
    [dictionariesByParent, i18n.language],
  );
  const jobLocationOptions = useMemo(
    () => [{ value: 'ANY', label: t('common.any', '不限') }, ...getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.JOB_LOCATION))],
    [dictionariesByParent, t, i18n.language],
  );
  const workTypeOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.WORK_TYPE)),
    [dictionariesByParent, i18n.language],
  );
  const jobTitleOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.JOB_TITLE)),
    [dictionariesByParent, i18n.language],
  );
  const educationOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.EDUCATION_LEVEL)),
    [dictionariesByParent, i18n.language],
  );
  const genderOptions = useMemo(
    () => getOptionsFromDictionaries(dictionariesByParent.get(DATA_DICTIONARY_TYPES.GENDER), GENDER_OPTIONS),
    [dictionariesByParent, i18n.language],
  );

  const {
    control,
    register,
    reset,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<HelperProfileFormValues, unknown, HelperProfileSubmitValues>({
    resolver: zodResolver(React.useMemo(() => buildHelperProfileSchema(t), [t])),
    defaultValues: buildDefaultValues(user),
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'workExperiences',
  });

  const [removeConfirmIndex, setRemoveConfirmIndex] = useState<number | null>(null);
  const [showOpenToWorkPrompt, setShowOpenToWorkPrompt] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<HelperProfileSubmitValues | null>(null);
  const [immediatelyAvailable, setImmediatelyAvailable] = useState(() => !getValues('availableStartDate'));
  const lastDateRef = React.useRef<string>(getValues('availableStartDate') || '');

  const performRemove = (index: number) => {
    remove(index);
    setExperienceDocs((prev) => {
      const next: ExperienceDocsMap = {};
      Object.entries(prev).forEach(([key, val]) => {
        const k = Number(key);
        if (k < index) next[k] = val;
        else if (k > index) next[k - 1] = val;
      });
      return next;
    });
    setRemoveConfirmIndex(null);
  };

  const handleRemoveExperience = (index: number) => {
    const docs = experienceDocs[index] ?? {};
    const hasPending = Object.values(docs).some((arr) => (arr ?? []).some((d) => !d.id));
    if (hasPending) {
      setRemoveConfirmIndex(index);
      return;
    }
    performRemove(index);
  };
  
  // Do not auto-open newly added experience entries
  const prevFieldsLengthRef = useRef(fields.length);
  useEffect(() => {
    prevFieldsLengthRef.current = fields.length;
  }, [fields.length, fields]);

  // Filter workExperiences[*].skills to valid WORK_EXPERIENCE_SKILL codes when dictionary loads
  useEffect(() => {
    const dictEntries = dictionariesByParent.get(DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL);
    if (!dictEntries || dictEntries.length === 0) return;
    const validCodes = new Set(dictEntries.map((d) => d.code));
    const currentExps = getValues('workExperiences');
    if (!currentExps) return;
    currentExps.forEach((exp, idx) => {
      if (!exp.skills) return;
      const filtered = exp.skills.filter((s) => validCodes.has(s));
      if (filtered.length !== exp.skills.length) {
        setValue(`workExperiences.${idx}.skills`, filtered, { shouldDirty: false });
      }
    });
  }, [dictionariesByParent]);

  const watchedValues = watch();

  const sectionCompletion = useMemo(
    () => ({
      general: getSectionCompletion('general', watchedValues, avatarUrl),
      about: getSectionCompletion('about', watchedValues, avatarUrl),
      education: getSectionCompletion('education', watchedValues, avatarUrl),
      'working-experience': getSectionCompletion('working-experience', watchedValues, avatarUrl),
      skills: getSectionCompletion('skills', watchedValues, avatarUrl),
    }),
    [watchedValues, avatarUrl],
  );

  useEffect(() => {
    if (!isEditMode && user) {
      setValue('userId', user.id);
      setValue('email', user.email || '');
    }
  }, [isEditMode, setValue, user]);

  useEffect(() => {
    apiService.jobPostings.getLegalMinimumWage()
      .then((res) => { if (res?.data) setLegalMinWage(res.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 220;

      for (let index = SECTION_LABELS.length - 1; index >= 0; index -= 1) {
        const sectionId = SECTION_LABELS[index].id;
        const element = document.getElementById(`section-${sectionId}`);

        if (element && scrollPosition >= element.offsetTop) {
          setActiveSection(sectionId);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  // Effect A: Fetch profile data once. Options deps excluded ??they're handled by Effect B.
  useEffect(() => {
    if (!isEditMode || !id) {
      return;
    }

    const loadProfile = async () => {
      try {
        setServerError('');
        const response = await apiService.helpers.getById(Number(id));

        if (response.success && response.data) {
          rawProfileRef.current = response.data;
          const values = mapProfileToFormValues(response.data, user, languageOptions, skillOptions);
          reset(values);
          setImmediatelyAvailable(!values.availableStartDate);
          replace(values.workExperiences);
          const docsMap: ExperienceDocsMap = {};
          (response.data.workExperiences ?? []).forEach((exp, i) => {
            (exp.documents ?? []).forEach((doc) => {
              const type = ((doc as HelperDocumentItem & { documentType?: string }).documentType ?? 'RECOMMENDATION_LETTER') as DocumentType;
              docsMap[i] = { ...docsMap[i], [type]: [...(docsMap[i]?.[type] ?? []), doc as unknown as HelperDocumentItem] };
            });
          });
          setExperienceDocs(docsMap);
          setAvatarUrl(response.data.avatarUrl || '');

          // Load profile-level documents (ID_PROOF, EDUCATION_CERT)
          const [idProofRes, eduCertRes] = await Promise.all([
            apiService.helpers.listDocuments(Number(id), 'ID_PROOF'),
            apiService.helpers.listDocuments(Number(id), 'EDUCATION_CERT'),
          ]);
          if (idProofRes.success && idProofRes.data) setIdProofDocuments(idProofRes.data);
          if (eduCertRes.success && eduCertRes.data) setEduCertDocuments(eduCertRes.data);
        }
      } catch (error) {
        console.error('Error loading helper profile:', error);
        setServerError(t('helperProfileForm.loadProfileFailed', '載入個人資料失敗'));
      } finally {
        setPageLoading(false);
      }
    };

    void loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- languageOptions/skillOptions handled by Effect B; reset/replace are stable refs
  }, [id, isEditMode]);

  // Effect B: When dictionary options change, silently re-map language/skill fields.
  // Does NOT touch pageLoading ??avoids the flash.
  useEffect(() => {
    if (!rawProfileRef.current || !isEditMode) return;

    const profile = rawProfileRef.current;
    const mappedLanguages = parseCommaString(profile.languageSkills);
    const mappedSkills = mapSelectionsFromOptions(
      parseCommaString(profile.skills),
      skillOptions,
    );

    setValue('languageSkills', mappedLanguages, { shouldValidate: false });
    setValue('otherSkills', mappedSkills, { shouldValidate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-map when options change
  }, [languageOptions, skillOptions]);

  const scrollToSection = (section: ActiveSection) => {
    const element = document.getElementById(`section-${section}`);
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(section);
  };

  const toggleArrayValue = <T extends string>(field: 'languageSkills' | 'otherSkills' | 'careTypes' | 'houseworkTypes' | 'cookingSkills', value: T) => {
    const currentValues = getValues(field) as string[];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];

    setValue(field, nextValues as HelperProfileFormValues[typeof field], { shouldValidate: true, shouldDirty: true });
  };

  const processAvatarFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      showAlert({
        type: 'error',
        message: t('helperProfile.avatar.invalidType', { defaultValue: 'Please select an image file' }),
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showAlert({
        type: 'error',
        message: t('helperProfile.avatar.invalidSize', { defaultValue: 'Image size cannot exceed 5MB' }),
      });
      return;
    }

    if (isEditMode && id) {
      try {
        setUploadingAvatar(true);
        const response = await apiService.helpers.uploadAvatar(Number(id), file);

        if (response.success && response.data) {
          setAvatarUrl(response.data.avatarUrl);
          showAlert({
            type: 'success',
            message: t('helperProfile.avatar.uploaded', { defaultValue: '?��?上傳?��?' }),
          });
        } else {
          showAlert({
            type: 'error',
            message: response.message || t('helperProfile.avatar.failed', { defaultValue: '?��?上傳失�?' }),
          });
        }
      } catch (error: any) {
        console.error('Error uploading avatar:', error);
        showAlert({
          type: 'error',
          message: error.response?.data?.message || t('helperProfile.avatar.failedRetry', { defaultValue: '?��?上傳失�?，�?稍�??�試' }),
        });
      } finally {
        setUploadingAvatar(false);
      }
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarUrl(reader.result as string);
      setPendingAvatarFile(file);
    };
    reader.readAsDataURL(file);

    showAlert({
      type: 'info',
      message: t('helperProfile.avatar.pending', { defaultValue: '?��?已選?��?保�??�人資�?後�??��?上傳?��?' }),
    });
  };

  const handleAvatarInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    await processAvatarFile(file);
    event.target.value = '';
  };

  const handleAvatarDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingAvatar(false);
    const file = event.dataTransfer.files?.[0];

    if (file) {
      await processAvatarFile(file);
    }
  };

  const handleAvatarRemove = () => {
    setAvatarUrl('');
    setPendingAvatarFile(null);
    showAlert({
      type: 'info',
      message: isEditMode
        ? t('helperProfile.avatar.removedEdit', { defaultValue: '?��?已移?��?請�?存以確�?' })
        : t('helperProfile.avatar.removedCreate', { defaultValue: '已移?��?上傳?��?' }),
    });
  };

  const onSubmit = async (values: HelperProfileSubmitValues) => {
    setServerError('');
    setOverlappingIndices([]);

    // If not open to work, prompt user before saving
    if (!values.lookingForJob) {
      setPendingSubmitValues(values);
      setShowOpenToWorkPrompt(true);
      return;
    }

    await doSave(values);
  };

  const doSave = async (values: HelperProfileSubmitValues) => {
    setServerError('');

    // WhatsApp 電話在此表單是唯讀（要去帳戶設定改），沒填的話後端一定會拒絕，
    // 先在這裡擋下來，避免使用者填完一整份表單才在送出時被打回票
    if (!values.whatsappPhone?.trim()) {
      const message = t('helperProfileForm.whatsappPhoneMissing', {
        defaultValue: '請先到帳戶設定填寫 WhatsApp 電話，才能儲存個人資料',
      });
      setServerError(message);
      showAlert({ type: 'error', message });
      return;
    }

    // 薪金不可低於法定最低工資
    if (legalMinWage && Number(values.expectedSalary) > 0 && Number(values.expectedSalary) < legalMinWage.amount) {
      const msg = t('helperProfileForm.salaryBelowMinimum', { currency: legalMinWage.currency, amount: legalMinWage.amount.toLocaleString() });
      showAlert({ type: 'error', message: msg });
      return;
    }

    // ?�?� Step 1: Base profile PUT/POST (no workExperiences in body) ?�?�?�?�?�?�?�?�?�?�
    const profilePayload = {
      userId: values.userId,
      fullName: values.fullName.trim(),
      gender: values.gender,
      height: values.height,
      weight: values.weight,
      dateOfBirth: values.dateOfBirth,
      nationality: values.nationality,
      passportNumber: values.passportNumber?.trim() || '',
      passportExpiryDate: values.passportExpiryDate || undefined,
      idNumber: values.idNumber?.trim() || '',
      whatsappPhone: values.whatsappPhone?.trim() || undefined,
      email: values.email?.trim() || undefined,
      maritalStatus: values.maritalStatus,
      educationLevel: values.educationLevel,
      languageSkills: toDelimitedString(values.languageSkills),
      workExperience: values.workExperience?.trim() || '',
      skills: toDelimitedString(values.otherSkills),
      cookingSkills: values.cookingSkillsText.map(s => s.split(':')[0]) as any,
      cookingSkillsText: values.cookingSkillsText.join(','),
      expectedSalary: values.expectedSalary,
      expectedSalaryCurrency: values.expectedSalaryCurrency,
      preferredWorkLocation: values.preferredWorkLocation?.trim() || undefined,
      selfIntroduction: values.selfIntroduction?.trim() || '',
      lookingForJob: values.lookingForJob,
      availableStartDate: values.availableStartDate || undefined,
      workType: values.workType || undefined,
      jobTitle: values.jobTitle || undefined,
      restDayPreference: values.restDayPreference,
      numberOfChildren: values.numberOfChildren,
      childrenAges: toDelimitedString(values.childrenAgesList.filter(Boolean)) || undefined,
      numberOfSiblings: values.numberOfSiblings,
      siblingRank: values.siblingRank,
      spouseOccupation: values.maritalStatus === 'MARRIED' ? (values.spouseOccupation?.trim() || undefined) : undefined,
      hometownRegion: values.hometownRegion?.trim() || undefined,
      religion: values.religion || undefined,
      careTypes: toDelimitedString(values.careTypes) || undefined,
      houseworkTypes: toDelimitedString(values.houseworkTypes) || undefined,
      educationInstitution: values.educationInstitution?.trim() || undefined,
      educationDegree: values.educationDegree?.trim() || undefined,
      educationStartYear: values.educationStartYear ? parseInt(values.educationStartYear, 10) : undefined,
      educationStartMonth: values.educationStartMonth ? parseInt(values.educationStartMonth, 10) : undefined,
      educationEndYear: values.educationEndYear ? parseInt(values.educationEndYear, 10) : undefined,
      educationEndMonth: values.educationEndMonth ? parseInt(values.educationEndMonth, 10) : undefined,
      educationOngoing: values.educationOngoing,
      accommodation: values.accommodation,
      willingWeekdayOff: values.willingWeekdayOff,
      eatPork: values.eatPork,
      willingCarePets: values.willingCarePets,
      tutoringExperience: values.tutoringExperience,
      needReturnHomeNextYear: values.needReturnHomeNextYear,
      previousAdultAtHome: values.previousAdultAtHome,
      previousCookDaily: values.previousCookDaily,
      willingCareNewborn: values.willingCareNewborn,
      willingCareChild: values.willingCareChild,
      willingCareElderly: values.willingCareElderly,
      willingCareDisabled: values.willingCareDisabled,
    };

    try {
      const profileResponse = isEditMode && id
        ? await apiService.helpers.update(Number(id), profilePayload as Partial<CreateHelperRequest>)
        : await apiService.helpers.create(profilePayload as unknown as CreateHelperRequest);

      if (!profileResponse.success) {
        const message = resolveApiError(profileResponse.errorCode, profileResponse.message, t) || t('helperProfileForm.saveFailed');
        setServerError(message);
        showAlert({ type: 'error', message });
        return;
      }

      // Soft warnings (e.g. SAME_NAME_DOB) — show non-blocking alerts
      profileResponse.warnings?.forEach(code => {
        const [warningCode, ...rest] = code.split(':');
        const phone = rest.join(':') || '****';
        const msg = t(`warnings.${warningCode}`, { phone, defaultValue: code });
        showAlert({ type: 'warning', message: msg });
      });

      const resolvedId: number | undefined = profileResponse.data?.id ?? (isEditMode && id ? Number(id) : undefined);

      // Upload pending avatar for newly created profiles
      if (!isEditMode && pendingAvatarFile && resolvedId) {
        try {
          setUploadingAvatar(true);
          const avatarResponse = await apiService.helpers.uploadAvatar(resolvedId, pendingAvatarFile);
          if (avatarResponse.success && avatarResponse.data) {
            setAvatarUrl(avatarResponse.data.avatarUrl);
            setPendingAvatarFile(null);
          }
        } catch {
          showAlert({
            type: 'warning',
            message: t('helperProfile.avatar.savedLater', { defaultValue: 'Profile saved, but avatar upload failed. Please try again later.' }),
          });
        } finally {
          setUploadingAvatar(false);
        }
      }

      if (!resolvedId) {
        showAlert({
          type: 'success',
          message: isEditMode
            ? t('helperProfile.updated', { defaultValue: 'Profile updated successfully!' })
            : t('helperProfile.created', { defaultValue: 'Profile created successfully!' }),
        });
        navigate('/profile');
        return;
      }

      // ?�?� Step 2: PUT /helpers/{id}/work-experiences (bulk replaceAll) ?�?�?�?�?�?�?�
      const parseYearMonth = (dateStr?: string): { year?: number; month?: number } => {
        if (!dateStr) return {};
        const parts = dateStr.split('-');
        const year = parts[0] ? parseInt(parts[0], 10) : undefined;
        const month = parts[1] ? parseInt(parts[1], 10) : undefined;
        return {
          year: year !== undefined && !Number.isNaN(year) ? year : undefined,
          month: month !== undefined && !Number.isNaN(month) ? month : undefined,
        };
      };

      // Retain original form indices alongside each valid experience for rec-letter mapping
      const validExpsWithIndex = values.workExperiences
        .map((exp, idx) => ({ exp, originalIndex: idx }))
        .filter(({ exp }) =>
          exp.startDate && (exp.title.trim() || exp.location.trim() || exp.employerFamily?.trim()),
        );

      // Sort by startYear DESC, startMonth DESC before sending
      validExpsWithIndex.sort((a, b) => {
        const aStart = parseYearMonth(a.exp.startDate);
        const bStart = parseYearMonth(b.exp.startDate);
        const yearDiff = (bStart.year ?? 0) - (aStart.year ?? 0);
        if (yearDiff !== 0) return yearDiff;
        return (bStart.month ?? 0) - (aStart.month ?? 0);
      });

      const workExpRequests = validExpsWithIndex.map(({ exp, originalIndex }) => {
        const start = parseYearMonth(exp.startDate);
        const end = exp.isCurrent ? {} : parseYearMonth(exp.endDate);
        const familySize: Record<string, number> = {};
        if (Number(exp.familySize.adults)) familySize['ADULT'] = Number(exp.familySize.adults);
        if (Number(exp.familySize.newborn)) familySize['NEWBORN'] = Number(exp.familySize.newborn);
        if (Number(exp.familySize.infant)) familySize['INFANT'] = Number(exp.familySize.infant);
        if (Number(exp.familySize.children)) familySize['CHILD'] = Number(exp.familySize.children);
        if (Number(exp.familySize.teen)) familySize['TEEN'] = Number(exp.familySize.teen);
        if (Number(exp.familySize.elderly)) familySize['ELDERLY'] = Number(exp.familySize.elderly);
        if (Number(exp.familySize.pets)) familySize['PET'] = Number(exp.familySize.pets);

        const docIds: number[] = [];
        for (const docType of ['RECOMMENDATION_LETTER', 'WORK_PROOF'] as const) {
          for (const doc of getDocs(originalIndex, docType)) {
            if (doc.id) docIds.push(doc.id);
          }
        }

        return {
          title: exp.title.trim(),
          employerFamily: exp.employerFamily?.trim() || undefined,
          country: exp.location.trim() || undefined,
          startYear: start.year,
          startMonth: start.month,
          endYear: exp.isCurrent ? undefined : end.year,
          endMonth: exp.isCurrent ? undefined : end.month,
          isCurrent: exp.isCurrent,
          familySize,
          unitSizeSqft: exp.unitSize,
          numberOfEmployers: exp.numberOfEmployers,
          specialNeedsNote: exp.notes.filter(Boolean).join('\n') || undefined,
          skillsUsed: exp.skills.filter(Boolean),
          hasRecommendationLetter: exp.hasRecommendationLetter ?? false,
          documentIds: docIds.length > 0 ? docIds : undefined,
        };
      });

      let savedExps: WorkExperience[] = [];
      try {
        const workExpResponse = await apiService.helpers.updateWorkExperiences(resolvedId, workExpRequests);
        if (!workExpResponse.success) {
          showAlert({ type: 'error', message: resolveApiError(workExpResponse.errorCode, workExpResponse.message, t) });
          return;
        }
        savedExps = workExpResponse.data ?? [];
      } catch (workExpError: any) {
        if (workExpError.response?.status === 422) {
          const errData = workExpError.response.data;
          if (errData?.code === 'OVERLAPPING_DATES' && Array.isArray(errData?.offendingIndices)) {
            // Map server-sorted indices back to original form indices
            const offendingOriginalIndices: number[] = errData.offendingIndices.map(
              (sortedIdx: number) => validExpsWithIndex[sortedIdx]?.originalIndex ?? sortedIdx,
            );
            setOverlappingIndices(offendingOriginalIndices);
            showAlert({
              type: 'error',
              message: t('helperProfile.overlappingDates', { defaultValue: '工作經驗的日期重疊，請修正後再提交' }),
            });
          } else {
            const msg = errData?.message || t('helperProfileForm.workExpUpdateFailed', '工作經驗更新失敗');
            setServerError(msg);
            showAlert({ type: 'error', message: msg });
          }
          return; // Abort; preserve unsaved upload state
        }
        throw workExpError; // Re-throw for outer catch
      }

      let hasPartialFailure = false;

      // ?�?� Step 3: POST documents for new experiences (all documentTypes) ?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�
      const UPLOAD_DOC_TYPES = ['RECOMMENDATION_LETTER', 'WORK_PROOF'] as const;

      for (let i = 0; i < validExpsWithIndex.length; i++) {
        const { originalIndex } = validExpsWithIndex[i];
        const workExperienceId = savedExps[i]?.id;
        if (!workExperienceId) continue;

        for (const docType of UPLOAD_DOC_TYPES) {
          const pendingDocs = getDocs(originalIndex, docType);

          for (const doc of pendingDocs) {
            if (doc.id || !doc.file) continue; // Already uploaded or no file to upload
            try {
              const formData = new FormData();
              formData.append('documentType', docType);
              formData.append('file', doc.file);
              formData.append('workExperienceId', String(workExperienceId));
              await apiService.helpers.uploadDocument(resolvedId, formData);
            } catch {
              hasPartialFailure = true;
            }
          }
        }
      }

      // ?�?� Step 4: DELETE orphaned docs (both RECOMMENDATION_LETTER and WORK_PROOF) ?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�
      const originalExps = rawProfileRef.current?.workExperiences ?? [];
      for (const origExp of originalExps) {
        if (!origExp.id) continue;
        // Find matching form entry by position (same order as loaded from server)
        const loadedIdx = originalExps.indexOf(origExp);
        const keptDocs: Record<string, boolean> = {};

        // Build a set of doc IDs that the user is KEEPING
        for (const docType of UPLOAD_DOC_TYPES) {
          const keptForType = getDocs(loadedIdx, docType);
          keptForType.forEach((d) => {
            if (d.id) keptDocs[String(d.id)] = true;
          });
        }

        const docsToDelete = origExp.documents ?? [];
        for (const doc of docsToDelete) {
          if (!doc.id || keptDocs[String(doc.id)]) continue;
          try {
            await apiService.helpers.deleteDocument(resolvedId, doc.id);
          } catch {
            hasPartialFailure = true;
          }
        }
      }

      // Upload profile-level documents (ID_PROOF, EDUCATION_CERT)
      for (const { docType, docs } of [
        { docType: 'ID_PROOF', docs: idProofDocuments },
        { docType: 'EDUCATION_CERT', docs: eduCertDocuments },
      ] as const) {
        for (const doc of docs) {
          if (doc.id || !doc.file) continue;
          try {
            const formData = new FormData();
            formData.append('documentType', docType);
            formData.append('file', doc.file);
            await apiService.helpers.uploadDocument(resolvedId, formData);
          } catch {
            hasPartialFailure = true;
          }
        }
      }

      // Success
      if (hasPartialFailure) {
        showAlert({
          type: 'warning',
          message: t('helperProfile.partialSuccess', { defaultValue: 'Profile saved, but some file actions failed. Please try again later.' }),
        });
      } else {
        showAlert({
          type: 'success',
          message: isEditMode
            ? t('helperProfile.updated', { defaultValue: 'Profile updated successfully!' })
            : t('helperProfile.created', { defaultValue: 'Profile created successfully!' }),
        });
      }
      navigate('/profile');
    } catch (error: any) {
      const data = error.response?.data;
      let message = data?.message || 'Operation failed, please try again later';
      // 後端驗證失敗時 data.error 是逐欄位錯誤訊息，附上讓使用者知道具體是哪裡沒填好
      if (typeof data?.error === 'string' && data.error.startsWith('{')) {
        message += `：${data.error.replace(/^\{|\}$/g, '')}`;
      }
      setServerError(message);
      showAlert({ type: 'error', message });
    }
  };

  const onValidationError = (errors: Record<string, unknown>) => {
    if (process.env.NODE_ENV === 'development') console.error('[Form validation errors]', errors);
    // 收集所有錯誤訊息給用戶
    const collectMessages = (obj: any, prefix = ''): string[] => {
      const msgs: string[] = [];
      if (!obj || typeof obj !== 'object') return msgs;
      for (const [key, val] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (val && typeof val === 'object') {
          if ((val as any).message) msgs.push(`${path}: ${(val as any).message}`);
          else msgs.push(...collectMessages(val, path));
        }
      }
      return msgs;
    };
    const msgs = collectMessages(errors);
    showAlert({
      type: 'error',
      message: msgs.length > 0
        ? `${t('helperProfileForm.fixFieldsPrefix', '請修正以下欄位：')}\n${msgs.slice(0, 5).join('\n')}${msgs.length > 5 ? `\n${t('helperProfileForm.moreItems', { n: msgs.length - 5 })}` : ''}`
        : t('helperProfileForm.formHasErrors', '表單有錯誤，請檢查必填項目'),
    });

    const expErrors = (errors as any).workExperiences as Record<string, unknown> | undefined;
    if (expErrors) {
      const firstErrorIdx = Object.keys(expErrors)
        .map(Number)
        .filter(n => !Number.isNaN(n))
        .sort((a, b) => a - b)[0];
      if (typeof firstErrorIdx === 'number') {
        const targetId = fields[firstErrorIdx]?.id;
        if (targetId) {
          setTimeout(() => {
            const node = document.querySelector(`[data-experience-id="${targetId}"]`);
            node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      }
    }
  };

  if (authLoading || pageLoading) {
    // Silent placeholder: keep the same background/min-height as the real form.
    return (
      <div className="min-h-screen bg-background" />
    );
  }

  return (
    <div className="min-h-screen bg-background px-3 py-4 sm:px-4 lg:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-3 overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-md backdrop-blur">
          <div className="px-4 py-4 lg:px-6">
            <h1 className="font-serif text-xl tracking-tight text-foreground">
              {t('helperProfileForm.editTitle', '編輯個人資料')}
            </h1>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <Card className="overflow-hidden rounded-2xl border-border bg-card text-card-foreground shadow-sm backdrop-blur">
              <CardHeader className="border-b border-border bg-card text-card-foreground">
                <CardTitle className="font-serif text-xl text-foreground">{t('helperProfileForm.navigation', '導覽')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 p-4">
                <nav className="space-y-2">
                  {SECTION_LABELS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition',
                        activeSection === section.id
                          ? 'border-primary bg-primary text-white shadow-lg'
                          : 'border-border bg-background text-foreground hover:border-primary/40 hover:text-primary',
                      )}
                    >
                      <div>
                        <p className="text-sm font-semibold">{t(section.labelKey, section.labelFallback)}</p>
                        <p className={cn('mt-1 text-xs', activeSection === section.id ? 'text-primary-foreground' : 'text-muted-foreground')}>
                          {t(section.descKey, section.descFallback)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-xs',
                            activeSection === section.id ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary/10 text-primary',
                          )}
                        >
                          {sectionCompletion[section.id]}%
                        </Badge>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </button>
                  ))}
                </nav>

                <div className="rounded-xl border border-amber-200/70 bg-card text-card-foreground p-4">
                    <p className="text-xs uppercase tracking-[0.28em] text-amber-700">{t('helperProfileForm.tips', 'Tips')}</p>
                    <h3 className="mt-3 font-serif text-xl text-foreground">{t('helperProfileForm.tipsTitle', 'Make employers stop scrolling.')}</h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {t('helperProfileForm.tipsDescription', 'Add a strong self-introduction, complete care categories, and structure household experience with exact dates.')}
                    </p>
                </div>
              </CardContent>
            </Card>
          </aside>

          <main>
            <ErrorBoundary>
              <form onSubmit={handleSubmit(onSubmit, onValidationError)} className="space-y-4">
              {serverError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{serverError}</div>
              ) : null}

              <Card id="section-general" className="rounded-2xl border-border bg-card text-card-foreground shadow-sm">
                <CardHeader className="pb-0">
                  <SectionHeading
                    icon={<UserCircle2 className="h-5 w-5" />}
                    title={t('helperProfileForm.generalInformation', 'General information')}
                    description={t('helperProfileForm.generalInformationDesc', 'Identity, contact details, responsibilities, salary expectations, and avatar upload live together here.')}
                    completion={sectionCompletion.general}
                  />
                </CardHeader>
                <CardContent className="space-y-8">
                  <Controller
                    control={control}
                    name="lookingForJob"
                    render={({ field }) => (
                      <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm font-medium text-foreground select-none">
                        <span>{t('common.lookingForJob')}</span>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </label>
                    )}
                  />
                  {user?.role !== 'HELPER' ? (
                    <div className="rounded-xl border border-border bg-muted p-4">
                      <FormLabel>{t('helperProfileForm.lookingFor', 'You are looking for...')}</FormLabel>
                      <div className="flex flex-wrap gap-3">
                        {[
                           { value: 'domestic-helper', label: t('helperProfileForm.domesticHelper', 'Domestic Helper') },
                           { value: 'driver', label: t('helperProfileForm.driver', 'Driver') },
                        ].map((option) => (
                          <PillButton key={option.value} active={jobType === option.value} onClick={() => setJobType(option.value as typeof jobType)}>
                            {option.label}
                          </PillButton>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
                    <div className="rounded-2xl border border-dashed border-border bg-card text-card-foreground p-4">
                      <FormLabel>{t('helperProfileForm.avatar', 'Avatar')}</FormLabel>
                      <div
                        onDragOver={(event) => {
                          event.preventDefault();
                          setIsDraggingAvatar(true);
                        }}
                        onDragLeave={() => setIsDraggingAvatar(false)}
                        onDrop={handleAvatarDrop}
                        className={cn(
                          'group flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-8 text-center transition',
                          isDraggingAvatar ? 'border-primary bg-primary/5' : 'border-border bg-card text-card-foreground hover:border-primary/40 hover:bg-background',
                        )}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleAvatarInputChange}
                          disabled={uploadingAvatar}
                        />

                        {avatarUrl ? (
                        <img src={avatarUrl} alt={t('helperProfileForm.avatarPreview', 'Avatar preview')} className="mb-6 h-24 w-24 rounded-2xl object-cover shadow-xl" />
                        ) : (
                          <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-2xl bg-muted text-muted-foreground shadow-inner">
                            <Camera className="h-12 w-12" />
                          </div>
                        )}

                        <p className="font-serif text-xl text-foreground">{t('helperProfileForm.portraitTitle', 'Portrait with presence')}</p>
                        <p className="mt-2 max-w-xs text-sm leading-6 text-muted-foreground">
                          {t('helperProfileForm.avatarDragDrop', 'Drag and drop a photo here, or click to upload.')}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">{t('helperProfile.avatarHint', 'JPG / PNG · max 5 MB')}</p>
                        <div className="mt-6 flex flex-wrap justify-center gap-3">
                          <Button type="button" variant="outline" disabled={uploadingAvatar}>
                            {uploadingAvatar ? t('helperProfileForm.uploading', 'Uploading...') : t('helperProfileForm.change', 'Change')}
                          </Button>
                          <Button type="button" variant="ghost" disabled={!avatarUrl || uploadingAvatar} onClick={(event) => {
                            event.stopPropagation();
                            handleAvatarRemove();
                          }}>
                            {t('helperProfileForm.remove', 'Remove')}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        <div className="md:col-span-2 xl:col-span-1">
                          <FormLabel>{t('helperProfileForm.name', 'Name')}</FormLabel>
                          <Input {...register('fullName')} placeholder={t('helperProfileForm.fullNamePlaceholder', 'Enter full name')} className="h-10 rounded-xl bg-background border-border" />
                          <FieldError message={errors.fullName?.message} />
                        </div>

                        <div>
                          <FormLabel>{t('common.gender')}</FormLabel>
                          <FormSelect control={control} name="gender" options={genderOptions} />
                        </div>

                        <div>
                          <FormLabel>{t('helperProfileForm.birth', 'Birth')}</FormLabel>
                          <Controller name="dateOfBirth" control={control} render={({ field }) => <DatePickerField value={field.value ?? ''} onChange={field.onChange} className="w-full" />} />
                          <FieldError message={errors.dateOfBirth?.message} />
                        </div>

                        <div>
                          <FormLabel>{t('common.nationality')}</FormLabel>
                           <FormSelect control={control} name="nationality" options={nationalityOptions} placeholder={t('helperProfileForm.select', 'Select')} />
                          <FieldError message={errors.nationality?.message} />
                        </div>

                        <div>
                          <FormLabel>{t('common.maritalStatus')}</FormLabel>
                          <FormSelect control={control} name="maritalStatus" options={maritalStatusOptions} />
                        </div>

                        <div>
                          <FormLabel>{t('helperProfileForm.numberOfChildren', 'Number of children')}</FormLabel>
                          <FormSelect control={control} name="numberOfChildren" options={CHILD_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
                        </div>

                        {Number(watch('numberOfChildren')) > 0 && (
                          <div className="md:col-span-2 xl:col-span-3">
                            <FormLabel>{t('helperProfileForm.childrenAges', 'Children ages')}</FormLabel>
                            <div className="flex flex-wrap gap-2">
                              {Array.from({ length: Number(watch('numberOfChildren')) || 0 }).map((_, idx) => (
                                <Input
                                  key={idx}
                                  type="number"
                                  min={0}
                                  max={60}
                                  value={watch('childrenAgesList')?.[idx] ?? ''}
                                  onChange={(e) => {
                                    const list = [...(watch('childrenAgesList') || [])];
                                    list[idx] = e.target.value;
                                    setValue('childrenAgesList', list);
                                  }}
                                  className="h-10 w-24 rounded-xl bg-background border-border"
                                  placeholder={t('helperProfileForm.childAgePlaceholder', `Child ${idx + 1} age`, { n: idx + 1 })}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <FormLabel>{t('common.religion')}</FormLabel>
                           <FormSelect control={control} name="religion" options={religionOptions} placeholder={t('helperProfileForm.select', 'Select')} />
                        </div>

                        <div>
                          <FormLabel>{t('helperProfileForm.numberOfSiblings', 'Number of siblings')}</FormLabel>
                          <Input type="number" min={0} {...register('numberOfSiblings', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })} className="h-10 rounded-xl bg-background border-border" />
                        </div>

                        <div>
                          <FormLabel>{t('helperProfileForm.siblingRank', 'Sibling rank (e.g. eldest = 1)')}</FormLabel>
                          <Input type="number" min={1} {...register('siblingRank', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })} className="h-10 rounded-xl bg-background border-border" />
                        </div>

                        {watch('maritalStatus') === 'MARRIED' && (
                          <div>
                            <FormLabel>{t('helperProfileForm.spouseOccupation', 'Spouse occupation')}</FormLabel>
                            <Input {...register('spouseOccupation')} className="h-10 rounded-xl bg-background border-border" />
                          </div>
                        )}

                        <div>
                          <FormLabel>{t('helperProfileForm.hometownRegion', 'Hometown region')}</FormLabel>
                          <Input {...register('hometownRegion')} placeholder={t('helperProfileForm.hometownRegionPlaceholder', 'e.g. Cebu, Philippines')} className="h-10 rounded-xl bg-background border-border" />
                        </div>

                        <div>
                          <FormLabel>{t('helperProfileForm.height', 'Height (cm)')}</FormLabel>
                        <Input type="number" {...register('height', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })} className="h-10 rounded-xl bg-background border-border" placeholder={t('helperProfileForm.heightPlaceholder', 'cm')} />
                        </div>

                        <div>
                          <FormLabel>{t('helperProfileForm.weight', 'Weight (kg)')}</FormLabel>
                        <Input type="number" {...register('weight', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })} className="h-10 rounded-xl bg-background border-border" placeholder={t('helperProfileForm.weightPlaceholder', 'kg')} />
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:col-span-2 xl:col-span-3">
                          <div>
                            <FormLabel>{t('helperProfileForm.passportNumber', 'Passport Number')}</FormLabel>
                            <Input {...register('passportNumber')} className="h-10 rounded-xl bg-background border-border" placeholder={t('helperProfileForm.passportPlaceholder', 'Enter passport number')} />
                          </div>

                          <div>
                            <FormLabel>{t('helperProfileForm.passportExpiryDate', 'Passport Expiry Date')}</FormLabel>
                            <Controller name="passportExpiryDate" control={control} render={({ field }) => <DatePickerField value={field.value ?? ''} onChange={field.onChange} className="w-full" startMonth={new Date(Date.now() - 7 * 86400000)} endMonth={new Date(new Date().getFullYear() + 20, 11)} />} />
                            <FieldError message={errors.passportExpiryDate?.message} />
                            {!errors.passportExpiryDate && watch('passportExpiryDate') && (() => {
                              const expiry = new Date(watch('passportExpiryDate') as string);
                              const sixMonthsFromNow = new Date();
                              sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
                              return expiry < sixMonthsFromNow ? (
                                <p className="mt-1 text-xs text-amber-600">{t('helperProfileForm.passportExpirySoon', '護照將於 6 個月內到期，建議盡快更新護照')}</p>
                              ) : null;
                            })()}
                          </div>

                          <div>
                            <FormLabel>{t('helperProfileForm.idNumber', '身分證號碼')}</FormLabel>
                            <Input {...register('idNumber')} className="h-10 rounded-xl bg-background border-border" placeholder={t('helperProfileForm.idNumberPlaceholder', '輸入身分證號碼')} />
                          </div>

                          <div>
                            <FormLabel>{t('helperProfileForm.whatsappPhone', '可接收Whatsapp的電話')}</FormLabel>
                            <Input {...register('whatsappPhone')} readOnly disabled className="h-10 rounded-xl bg-muted border-border cursor-not-allowed opacity-70" />
                            <p className="mt-1 text-xs text-muted-foreground">{t('helperProfileForm.editInSettings', '請於帳戶設定中修改')}</p>
                          </div>

                          <div>
                            <FormLabel>{t('helperProfileForm.secondaryPhone', '副電話')}</FormLabel>
                            <Input {...register('secondaryPhone')} className="h-10 rounded-xl bg-background border-border" placeholder="e.g. +85298765432" />
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-xl border border-border bg-muted/50 p-4">
                      <FormLabel>{t('helperProfileForm.responsibilitiesCare', 'Responsibilities · Care')}</FormLabel>
                      <div className="flex flex-wrap gap-2.5">
                        {careOptions.map((option) => (
                          <PillButton
                            key={option.value}
                            active={watchedValues.careTypes.includes(option.value)}
                            onClick={() => toggleArrayValue('careTypes', option.value)}
                          >
                            {option.label}
                          </PillButton>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-muted/50 p-4">
                      <FormLabel>{t('helperProfileForm.responsibilitiesHousework', 'Responsibilities · Housework')}</FormLabel>
                      <div className="flex flex-wrap gap-2.5">
                        {houseworkOptions.map((option) => (
                          <PillButton
                            key={option.value}
                            active={watchedValues.houseworkTypes.includes(option.value)}
                            onClick={() => toggleArrayValue('houseworkTypes', option.value)}
                          >
                            {option.label}
                          </PillButton>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 rounded-xl border border-border bg-card text-card-foreground p-6">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary/70">{t('helperProfileForm.expectations', 'Expectations')}</p>
                        <p className="text-sm text-muted-foreground">{t('helperProfileForm.expectationsDesc', 'Salary, rest day, accommodation, work location, and market readiness.')}</p>
                      </div>
                      <div className="rounded-full bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-800">
                        {t('helperProfileForm.hksarNotice', 'According to HKSAR law, domestic helpers are not allowed to live out.')}
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <FormLabel>{t('helperProfileForm.jobTitle', '你在尋找')}</FormLabel>
                        <FormSelect control={control} name="jobTitle" options={jobTitleOptions} placeholder={t('helperProfileForm.select', '請選擇')} />
                      </div>

                      <div>
                        <FormLabel>{t('helperProfileForm.workType', '工作形式')}</FormLabel>
                        <FormSelect control={control} name="workType" options={workTypeOptions} placeholder={t('common.any', '不限')} />
                      </div>

                      <div>
                        <FormLabel>{t('helperProfileForm.salaryCurrency', 'Salary Currency')}</FormLabel>
                        <FormSelect control={control} name="expectedSalaryCurrency" options={SALARY_CURRENCY_OPTIONS} />
                      </div>

                      <div>
                        <FormLabel>{t('helperProfileForm.expectedSalary', 'Expected Salary')}</FormLabel>
                        <Input
                          type="number"
                          {...register('expectedSalary', { setValueAs: (value) => Number(value) || 0 })}
                          className="h-10 rounded-xl bg-background border-border"
                          placeholder="0"
                          onBlur={(e) => {
                            const val = Number(e.target.value);
                            if (legalMinWage && val > 0 && val < legalMinWage.amount) {
                              setValue('expectedSalary', legalMinWage.amount, { shouldDirty: true });
                              showAlert({ type: 'info', message: t('helperProfileForm.salaryAutoAdjusted', { currency: legalMinWage.currency, amount: legalMinWage.amount.toLocaleString() }) });
                            }
                          }}
                        />
                        {legalMinWage && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t('jobForm.legalMinHint', { currency: legalMinWage.currency, amount: legalMinWage.amount.toLocaleString() })}
                          </p>
                        )}
                      </div>

                      <div>
                        <FormLabel>{t('helperProfileForm.preferredWorkLocation', 'Preferred work location')}</FormLabel>
                        <FormSelect control={control} name="preferredWorkLocation" options={jobLocationOptions} placeholder="Select" />
                      </div>

                      <div>
                        <FormLabel>{t('helperProfileForm.dayOff', 'Day Off')}</FormLabel>
                        <FormSelect control={control} name="restDayPreference" options={getRestDayOptions(t)} />
                      </div>

                      <div>
                        <FormLabel>{t('helperProfileForm.accommodation', 'Accommodation')}</FormLabel>
                        <FormSelect control={control} name="accommodation" options={accommodationOptions} />
                      </div>

                      <div>
                        <FormLabel>{t('helperProfileForm.availableStartDate', 'Available start date')}</FormLabel>
                        <Controller name="availableStartDate" control={control} render={({ field }) => (
                          <DatePickerField
                            value={immediatelyAvailable ? '' : (field.value ?? '')}
                            onChange={(v) => {
                              if (v) lastDateRef.current = v;
                              field.onChange(v);
                              if (v) setImmediatelyAvailable(false);
                            }}
                            className="w-full"
                            startMonth={new Date()}
                            endMonth={new Date(new Date().getFullYear() + 5, 11)}
                            disabled={immediatelyAvailable}
                          />
                        )} />
                      </div>

                      <div className="flex items-end pb-0.5">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={immediatelyAvailable}
                            onCheckedChange={(checked) => {
                              const val = !!checked;
                              setImmediatelyAvailable(val);
                              if (val) {
                                lastDateRef.current = getValues('availableStartDate') || lastDateRef.current;
                                setValue('availableStartDate', '', { shouldDirty: true });
                              } else {
                                setValue('availableStartDate', lastDateRef.current, { shouldDirty: true });
                              }
                            }}
                          />
                          {t('helperProfileForm.immediatelyAvailable', 'Immediately available')}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* 其他資料 toggles */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground">{t('helperProfileForm.otherInfo', '其他資料')}</h3>
                    <div className="grid gap-x-8 md:grid-cols-2">
                    {[
                      { name: 'willingCareNewborn' as const, label: t('helperProfileForm.toggleCareNewborn') },
                      { name: 'willingCareChild' as const, label: t('helperProfileForm.toggleCareChild') },
                      { name: 'willingCareElderly' as const, label: t('helperProfileForm.toggleCareElderly') },
                      { name: 'willingCareDisabled' as const, label: t('helperProfileForm.toggleCareDisabled') },
                      { name: 'willingCarePets' as const, label: t('helperProfileForm.toggleCarePets') },
                      { name: 'willingWeekdayOff' as const, label: t('helperProfileForm.toggleWeekdayOff') },
                      { name: 'eatPork' as const, label: t('helperProfileForm.toggleEatPork') },
                      { name: 'tutoringExperience' as const, label: t('helperProfileForm.toggleTutoring') },
                      { name: 'previousAdultAtHome' as const, label: t('helperProfileForm.togglePrevAdultAtHome') },
                      { name: 'previousCookDaily' as const, label: t('helperProfileForm.togglePrevCookDaily') },
                      { name: 'needReturnHomeNextYear' as const, label: t('helperProfileForm.toggleReturnHomeNextYear') },
                    ].map(({ name, label }) => (
                      <Controller
                        key={name}
                        name={name}
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center justify-between gap-3 py-2 border-b border-border/40">
                            <span className="text-sm text-foreground/80">{label}</span>
                            <Switch
                              checked={field.value === true}
                              onCheckedChange={(checked) => field.onChange(checked)}
                            />
                          </div>
                        )}
                      />
                    ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <Separator className="my-3" />
                    <p className="text-xs font-semibold text-muted-foreground mb-3">{t('helperProfileForm.idProofDocuments', '身份證明文件 (ID Proof)')}</p>
                    <FileUploadList
                      documentType="ID_PROOF"
                      helperId={id ? Number(id) : 0}
                      documents={idProofDocuments}
                      onChange={setIdProofDocuments}
                    />
                  </div>
                </CardContent>
              </Card>

              <div id="section-about" className="space-y-8">
              <Card className="gap-3 rounded-2xl border-border bg-card text-card-foreground shadow-sm">
                <CardHeader className="pb-0">
                  <SectionHeading
                    icon={<Sparkles className="h-5 w-5" />}
                      title={t('helperProfileForm.about', 'About')}
                      description={t('helperProfileForm.aboutDesc', 'Add warmth, confidence, and availability details that give the profile personality.')}
                    completion={sectionCompletion.about}
                  />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <FormLabel>{t('helperProfileForm.selfIntroduction', 'Self introduction')}</FormLabel>
                    <Textarea className="bg-background border-border min-h-[120px]"
                      {...register('selfIntroduction')}
                      rows={8}
                      placeholder={t('helperProfileForm.selfIntroductionPlaceholder', 'Tell employers about your strengths, routines, family care experience, and the household you work best with.')}
                    />
                    <FieldError message={errors.selfIntroduction?.message} />
                  </div>
                </CardContent>
              </Card>

              <Card className="gap-3 rounded-2xl border-border bg-card text-card-foreground shadow-sm">
                <CardHeader className="pb-0">
                  <SectionHeading
                    icon={<GraduationCap className="h-5 w-5" />}
                      title={t('helperProfileForm.education', 'Education')}
                      description={t('helperProfileForm.educationDesc', 'Education level plus institution, degree, and period give the profile additional trust signals.')}
                    completion={sectionCompletion.education}
                  />
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-3">
                  <div>
                     <FormLabel>{t('common.educationLevel')}</FormLabel>
                    <FormSelect control={control} name="educationLevel" options={educationOptions} placeholder="Select education level" />
                    <FieldError message={errors.educationLevel?.message} />
                  </div>

                  <div>
                     <FormLabel>{t('helperProfileForm.institution', 'Institution')}</FormLabel>
                       <Input {...register('educationInstitution')} className="h-10 rounded-xl bg-background border-border" placeholder={t('helperProfileForm.institutionPlaceholder', 'e.g. Arena Multimedia')} />
                  </div>

                  <div>
                     <FormLabel>{t('helperProfileForm.degreeDiploma', 'Degree / Diploma')}</FormLabel>
                       <Input {...register('educationDegree')} className="h-10 rounded-xl bg-background border-border" placeholder={t('helperProfileForm.degreePlaceholder', 'e.g. Advanced Diploma in Multimedia')} />
                  </div>

                  <div className="md:col-span-3">
                     <FormLabel>{t('helperProfileForm.period', 'Period')}</FormLabel>
                     <div className="flex flex-col gap-4 mt-2">
                        <div className="flex items-center space-x-2">
                          <Controller
                            name="educationOngoing"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                id="educationOngoing"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            )}
                          />
                          <label
                            htmlFor="educationOngoing"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {t('helperProfileForm.educationOngoing', '?��?�?(Ongoing)')}
                          </label>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                           <div className="flex gap-2">
                              <Controller
                                name="educationStartYear"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    value={field.value ? String(field.value) : undefined}
                                    onValueChange={(val) => field.onChange(Number(val))}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder={t('helperProfileForm.year', 'Year')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Array.from({length: new Date().getFullYear() + 1 - 1970 + 1}, (_, i) => 1970 + i).reverse().map(year => (
                                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                              <Controller
                                name="educationStartMonth"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    value={field.value ? String(field.value) : undefined}
                                    onValueChange={(val) => field.onChange(Number(val))}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder={t('helperProfileForm.month', 'Month')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                                        <SelectItem key={month} value={String(month)}>{String(month).padStart(2, '0')}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                           </div>
                           <div className="flex gap-2">
                              <Controller
                                name="educationEndYear"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    disabled={watch('educationOngoing')}
                                    value={field.value ? String(field.value) : undefined}
                                    onValueChange={(val) => field.onChange(Number(val))}
                                  >
                                    <SelectTrigger className="w-full" disabled={watch('educationOngoing')}>
                                      <SelectValue placeholder={t('helperProfileForm.year', 'Year')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Array.from({length: new Date().getFullYear() + 1 - 1970 + 1}, (_, i) => 1970 + i).reverse().map(year => (
                                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                              <Controller
                                name="educationEndMonth"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    disabled={watch('educationOngoing')}
                                    value={field.value ? String(field.value) : undefined}
                                    onValueChange={(val) => field.onChange(Number(val))}
                                  >
                                    <SelectTrigger className="w-full" disabled={watch('educationOngoing')}>
                                      <SelectValue placeholder={t('helperProfileForm.month', 'Month')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                                        <SelectItem key={month} value={String(month)}>{String(month).padStart(2, '0')}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                           </div>
                        </div>
                        {errors.educationEndYear && (
                           <p className="text-sm font-medium text-destructive">{errors.educationEndYear.message}</p>
                        )}
                     </div>
                  </div>
                  <div className="md:col-span-3 pt-2">
                    <Separator className="my-3" />
                    <p className="text-xs font-semibold text-muted-foreground mb-3">{t('helperProfileForm.educationDocuments', '學歷及專業證書（急救、陪月、護理等）')}</p>
                    <FileUploadList
                      documentType="EDUCATION_CERT"
                      helperId={id ? Number(id) : 0}
                      documents={eduCertDocuments}
                      onChange={setEduCertDocuments}
                    />
                  </div>
                </CardContent>
              </Card>
              </div>

              <div id="section-working-experience" className="space-y-8">
<Card className="gap-3 rounded-2xl border-border bg-card text-card-foreground shadow-sm">
                <CardHeader className="pb-0">
                  <SectionHeading
                    icon={<Briefcase className="h-5 w-5" />}
                      title={t('helperProfileForm.workingExperiences', 'Working experiences')}
                      description={t('helperProfileForm.workingExperiencesDesc', 'Structured household records improve trust. Add each role in backward order and keep timelines clear.')}
                    completion={sectionCompletion['working-experience']}
                  />
                </CardHeader>
                <CardContent className="space-y-3 p-4">
                  <div className="space-y-3">
  {fields.map((field, index) => {
    const currentExperience = watchedValues.workExperiences[index];
    return (
      <div
        key={field.id}
        data-experience-id={field.id}
        className="rounded-xl border border-border bg-card overflow-hidden shadow-sm"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate font-medium text-foreground text-sm">
                {t('helperProfileForm.experienceLabel', 'Experience')} {index + 1}
              </p>
              <Badge className="shrink-0 rounded-full bg-amber-100 px-2 py-0 text-xs leading-4 text-amber-800 hover:bg-amber-100">
                {currentExperience?.isCurrent
                  ? t('helperProfileForm.currentRole', 'Current role')
                  : t('helperProfileForm.pastRole', 'Past role')}
              </Badge>
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {formatExperienceSummary(currentExperience, t)}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => handleRemoveExperience(index)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="px-3 py-2.5 space-y-2.5">
          <label className="flex items-center justify-between rounded-lg border border-border bg-muted/60 px-3 py-1.5 text-xs font-medium text-foreground">
            <span>{t('helperProfileForm.currentWorking', 'I am currently working in this role')}</span>
            <Controller control={control} name={`workExperiences.${index}.isCurrent`} render={({field}) => <Checkbox checked={field.value} onCheckedChange={field.onChange} />} />
          </label>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.title', 'Title')}</p>
              <Input {...register(`workExperiences.${index}.title` as const)} className="h-8 rounded-lg bg-background border-border text-sm" placeholder={t('helperProfileForm.titlePlaceholder', 'e.g. Nanny')} />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.employerFamily', 'Employer family')}</p>
              <Input {...register(`workExperiences.${index}.employerFamily` as const)} className="h-8 rounded-lg bg-background border-border text-sm" placeholder={t('helperProfileForm.employerFamilyPlaceholder', 'e.g. Filipino Family')} />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.location', 'Location')}</p>
              <FormSelect control={control} name={`workExperiences.${index}.location`} options={WORK_EXPERIENCE_LOCATIONS.map(l => ({ value: l.value, label: t(l.labelKey) }))} placeholder={t('helperProfileForm.select', 'Select')} />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.unitSize', 'Unit size (sq ft)')}</p>
              <Input type="number" min={0} {...register(`workExperiences.${index}.unitSize` as const, { setValueAs: (value) => (value === '' ? undefined : Number(value)) })} className="h-8 rounded-lg bg-background border-border text-sm" />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.numberOfEmployers', '外傭數目')}</p>
              <Input type="number" min={0} {...register(`workExperiences.${index}.numberOfEmployers` as const, { setValueAs: (value) => (value === '' ? undefined : Number(value)) })} className="h-8 rounded-lg bg-background border-border text-sm" />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.startDate', 'Start date')}</p>
              <Input type="month" {...register(`workExperiences.${index}.startDate` as const)} className="h-8 rounded-lg bg-background border-border text-sm" />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">
                {currentExperience?.isCurrent ? t('helperProfileForm.expectedEndDate', '預計結束日子') : t('helperProfileForm.endDate', 'End date')}
              </p>
              <Input type="month" {...register(`workExperiences.${index}.endDate` as const)} className="h-8 rounded-lg bg-background border-border text-sm" />
            </div>
            {!currentExperience?.isCurrent && (
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.contractStatus', '合約狀態')}</p>
                <select
                  {...register(`workExperiences.${index}.contractEndReason` as const)}
                  className="h-8 w-full rounded-lg bg-background border border-border px-2 text-sm"
                >
                  <option value="">{t('helperProfileForm.select', '請選擇')}</option>
                  <option value="COMPLETED">{t('helperProfileForm.contractCompleted', '完結')}</option>
                  <option value="EARLY_TERMINATION">{t('helperProfileForm.contractEarlyTermination', '斷約')}</option>
                  <option value="DISMISSED">{t('helperProfileForm.contractDismissed', '解僱')}</option>
                  <option value="OTHER">{t('helperProfileForm.contractOtherReason', '其他原因')}</option>
                </select>
              </div>
            )}
          </div>
          {!currentExperience?.isCurrent && currentExperience?.contractEndReason === 'OTHER' && (
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.explainReason', '請說明原因')}</p>
              <Input
                {...register(`workExperiences.${index}.contractEndReasonNote` as const)}
                className="h-8 rounded-lg bg-background border-border text-sm"
                placeholder={t('helperProfileForm.reasonPlaceholder', '請輸入原因...')}
              />
            </div>
          )}

          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
            <div className="mb-1.5 flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-primary" />
              <p className="text-xs font-semibold text-foreground">{t('helperProfileForm.familyAtDeparture', '家庭成員 (以離職時計)')}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-4">
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.familyAdults', '成人')}</p>
                <FormSelect control={control} name={`workExperiences.${index}.familySize.adults`} options={FAMILY_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.familyNewborn', '初生 (0-6個月)')}</p>
                <FormSelect control={control} name={`workExperiences.${index}.familySize.newborn`} options={FAMILY_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.familyInfant', '嬰兒 (6個月-3歲)')}</p>
                <FormSelect control={control} name={`workExperiences.${index}.familySize.infant`} options={FAMILY_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.familyChild', '小童 (3-12歲)')}</p>
                <FormSelect control={control} name={`workExperiences.${index}.familySize.children`} options={FAMILY_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.familyTeen', '少年 (12-18歲)')}</p>
                <FormSelect control={control} name={`workExperiences.${index}.familySize.teen`} options={FAMILY_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.familyElderly', '長者 (65歲以上)')}</p>
                <FormSelect control={control} name={`workExperiences.${index}.familySize.elderly`} options={FAMILY_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.familyPets', '寵物')}</p>
                <FormSelect control={control} name={`workExperiences.${index}.familySize.pets`} options={FAMILY_COUNT_OPTIONS} setValueAs={(v) => Number(v) || 0} />
              </div>
            </div>
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.skillsUsed', 'Skills used in this role')}</p>
              <MultiSelectPills
                options={(dictionariesByParent.get(DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL) ?? []).map((d) => ({
                  code: d.code,
                  labelZh: d.valueChinese,
                  labelEn: d.valueEnglish,
                }))}
                value={currentExperience?.skills ?? []}
                onChange={(vals) => setValue(`workExperiences.${index}.skills`, vals, { shouldDirty: true })}
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">{t('helperProfileForm.notes', 'Notes')}</p>
              <Input
                value={(currentExperience?.notes ?? []).join(' · ')}
                onChange={(event) => {
                  setValue(
                    `workExperiences.${index}.notes`,
                    event.target.value.split(/\n|·|,/).map((item) => item.trim()).filter(Boolean),
                    { shouldDirty: true },
                  );
                }}
                className="h-8 rounded-lg bg-background border-border text-sm"
                placeholder="Newborn care · marketing · pet care"
              />
            </div>
          </div>

          {/* ── Document uploads ──────────────────────────────────── */}
          {!(isEditMode && id) && (
            <p className="text-xs text-amber-700">
              {t('helperProfileForm.saveFirstToUpload', 'Save the profile once to enable file uploads.')}
            </p>
          )}
          <div className="grid gap-2 md:grid-cols-2">
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
              <p className="mb-1 text-xs font-semibold text-foreground">
                {t('helperProfileForm.recommendationLetterTitle', 'Recommendation letter')}
              </p>
              <FileUploadList
                helperId={isEditMode && id ? Number(id) : 0}
                documentType="RECOMMENDATION_LETTER"
                workExperienceId={currentExperience?.id || undefined}
                documents={getDocs(index, 'RECOMMENDATION_LETTER')}
                onChange={(docs) => setDocs(index, 'RECOMMENDATION_LETTER', docs)}
                disabled={!(isEditMode && id)}
              />
            </div>
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
              <p className="mb-1 text-xs font-semibold text-foreground">
                {t('helperProfileForm.otherDocumentsTitle', 'Other supporting documents')}
              </p>
              <FileUploadList
                helperId={isEditMode && id ? Number(id) : 0}
                documentType="WORK_PROOF"
                workExperienceId={currentExperience?.id || undefined}
                documents={getDocs(index, 'WORK_PROOF')}
                onChange={(docs) => setDocs(index, 'WORK_PROOF', docs)}
                disabled={!(isEditMode && id)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  })}
</div>
                  <div className="flex justify-end mt-3">
                    <Button type="button" onClick={() => append(createEmptyWorkExperience())} className="rounded-full px-5">
                      {t('helperProfileForm.addWorkExperience', '+ Add work experience')}
                    </Button>
                  </div>

                </CardContent>
                <AlertDialog open={showOpenToWorkPrompt} onOpenChange={setShowOpenToWorkPrompt}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('helperProfileForm.openToWorkPromptTitle', '您目前沒有開放工作機會')}</AlertDialogTitle>
                      <AlertDialogDescription>{t('helperProfileForm.openToWorkPromptBody', '您尚未勾選「開放工作機會」，其他人將無法在搜尋中找到您。是否要開啟此選項？')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => {
                        setShowOpenToWorkPrompt(false);
                        if (pendingSubmitValues) doSave(pendingSubmitValues);
                      }}>
                        {t('helperProfileForm.openToWorkNo', '我目前不找工作，繼續儲存')}
                      </AlertDialogCancel>
                      <AlertDialogAction onClick={() => {
                        setValue('lookingForJob', true);
                        setShowOpenToWorkPrompt(false);
                        if (pendingSubmitValues) doSave({ ...pendingSubmitValues, lookingForJob: true });
                      }}>
                        {t('helperProfileForm.openToWorkYes', '開啟「開放工作機會」')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog open={removeConfirmIndex !== null} onOpenChange={(open) => { if (!open) setRemoveConfirmIndex(null); }}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('helperProfileForm.removeExperienceConfirmTitle', 'Remove this experience?')}</AlertDialogTitle>
                      <AlertDialogDescription>{t('helperProfileForm.removeExperienceConfirmBody', 'This entry has files queued for upload that will be discarded.')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => setRemoveConfirmIndex(null)}>
                        {t('common.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => { if (removeConfirmIndex !== null) performRemove(removeConfirmIndex); }}
                      >
                        {t('helperProfileForm.removeExperienceConfirmCta', 'Remove anyway')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </Card>

              <Card className="rounded-2xl border-border bg-card text-card-foreground shadow-sm">
                <CardHeader className="pb-0">
                  <SectionHeading
                    icon={<MapPin className="h-5 w-5" />}
                      title={t('common.skills')}
                      description={t('helperProfileForm.skillsDesc', 'Multi-select skill tags keep the profile skimmable and searchable across language, care, cooking, and household tasks.')}
                    completion={sectionCompletion.skills}
                  />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-xl border border-border bg-muted/50 p-4">
                    <FormLabel>{t('helperProfileForm.languageSkills', '語言能力')}</FormLabel>
                    <div className="mt-2 grid gap-x-4 gap-y-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                      {languageOptions.map((option) => {
                        const current = watchedValues.languageSkills.find(s => s.startsWith(option.value + ':') || s === option.value);
                        const currentLevel = current ? current.split(':')[1] ?? '' : '';
                        return (
                          <div key={option.value} className="flex items-center gap-2">
                            <span className="w-20 shrink-0 text-sm">{option.label}</span>
                            <Select
                              value={currentLevel}
                              onValueChange={(level) => {
                                const next = watchedValues.languageSkills.filter(s => !s.startsWith(option.value + ':') && s !== option.value);
                                if (level) next.push(`${option.value}:${level}`);
                                setValue('languageSkills', next, { shouldValidate: true });
                              }}
                            >
                              <SelectTrigger className="h-8 w-36 text-sm">
                                <SelectValue placeholder={t('helperProfileForm.notSelected', '未選擇')} />
                              </SelectTrigger>
                              <SelectContent>
                                {proficiencyOptions.map(p => (
                                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                    <FieldError message={errors.languageSkills?.message} />
                  </div>

                  <div className="rounded-xl border border-border bg-muted/50 p-4">
                    <div className="flex items-center gap-1">
                      <FormLabel>{t('common.cookingSkills')}</FormLabel>
                      <CookingProficiencyTooltip />
                    </div>
                    <div className="mt-2 grid gap-x-4 gap-y-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                      {cookingOptions.map((option) => {
                        const current = watchedValues.cookingSkillsText.find(s => s.startsWith(option.value + ':') || s === option.value);
                        const currentLevel = current ? current.split(':')[1] ?? '' : '';
                        return (
                          <div key={option.value} className="flex items-center gap-2">
                            <span className="w-20 shrink-0 text-sm">{option.label}</span>
                            <Select
                              value={currentLevel}
                              onValueChange={(level) => {
                                const next = watchedValues.cookingSkillsText.filter(s => !s.startsWith(option.value + ':') && s !== option.value);
                                if (level) next.push(`${option.value}:${level}`);
                                setValue('cookingSkillsText', next, { shouldValidate: true });
                              }}
                            >
                              <SelectTrigger className="h-8 w-36 text-sm">
                                <SelectValue placeholder={t('helperProfileForm.notSelected', '未選擇')} />
                              </SelectTrigger>
                              <SelectContent>
                                {cookingProficiencyOptions.map(p => (
                                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-muted/50 p-4">
                     <FormLabel>{t('helperProfileForm.otherSkills', 'Other Skills')}</FormLabel>
                    <div className="flex flex-wrap gap-2.5">
                      {skillOptions.map((option) => (
                        <PillButton
                          key={option.value}
                          active={watchedValues.otherSkills.includes(option.value)}
                          onClick={() => toggleArrayValue('otherSkills', option.value)}
                        >
                          {option.label}
                        </PillButton>
                      ))}
                    </div>
                    <FieldError message={errors.otherSkills?.message} />
                  </div>
                </CardContent>
              </Card>

              </div>

              <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card text-card-foreground p-4 shadow-sm sm:flex-row sm:items-center sm:justify-end">
                <Button type="button" variant="outline" className="rounded-full px-6" onClick={() => navigate('/profile')}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="rounded-full px-6" disabled={isSubmitting || uploadingAvatar}>
                  <Save className="h-4 w-4" />
                  {isSubmitting ? t('helperProfileForm.saving', 'Saving...') : t('common.save')}
                </Button>
              </div>
              </form>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
};

export default HelperProfileFormPage;
